import React from "react";
import {
  OnboardingStep,
  OnboardingData,
} from "@/hooks/use-onboarding";
import { CountryPicker } from "@/components/onboarding/country-picker";
import { UsernameInput } from "@/components/onboarding/username-input";
import { StarterTypePicker } from "@/components/onboarding/starter-type-picker";
import { StarterReveal } from "@/components/onboarding/starter-reveal";
import { Button } from "@/components/ui/buttons/button";
import { IntroView } from "./intro-view";
import { RPGConversationLayout } from "@/components/onboarding/rpg-conversation-layout";
import { getCountryName } from "@/data/countries";

interface OnboardingViewProps {
  step: OnboardingStep;
  nextStep: () => void;
  data: OnboardingData;
  updateData: (newData: Partial<OnboardingData>) => void;
  isLoading?: boolean;
  error?: string | null;
  pickStarter: (types: string[]) => Promise<void>;
  createPlayer: (username: string) => Promise<boolean>;
  verifyUsername: (username: string) => Promise<boolean>;
  updateCountry: (country: string) => Promise<void>;
  completeOnboarding: () => Promise<void>;
}

export const OnboardingView: React.FC<OnboardingViewProps> = ({
  step,
  nextStep,
  data,
  updateData,
  isLoading,
  error,
  pickStarter,
  createPlayer,
  verifyUsername,
  updateCountry,
  completeOnboarding,
}) => {
  const [currentRevealIndex, setCurrentRevealIndex] = React.useState(0);

  const onSelectStarter = async (types: string[]) => {
    updateData({ starterTypes: types });
    await pickStarter(types);
    nextStep();
  };

  const onRevealComplete = () => {
    if (data.starters && currentRevealIndex < data.starters.length - 1) {
      setCurrentRevealIndex((prev) => prev + 1);
    } else {
      nextStep();
    }
  };

  const renderStep = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-pk-blue-3 border-t-transparent rounded-full animate-spin" />
          <p className="text-white font-black italic animate-pulse uppercase tracking-widest">
            Processing...
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center gap-6 p-8 bg-red-900/40 backdrop-blur-md rounded-3xl border border-red-500/50 max-w-md w-full text-center">
          <h2 className="text-2xl font-black italic text-white uppercase">
            Error
          </h2>
          <p className="text-red-200 font-medium">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-8 py-3 bg-white text-red-900 font-black rounded-xl uppercase tracking-widest"
          >
            Retry
          </button>
        </div>
      );
    }

    switch (step) {
      case "country_picker":
        return (
          <RPGConversationLayout text="In your world, where are you from?">
            <CountryPicker
              onSelect={async (country) => {
                updateData({ country });
                await updateCountry(country);
                nextStep();
              }}
            />
          </RPGConversationLayout>
        );

      case "username_input":
        return (
          <RPGConversationLayout text="Before anything else, how should I call you?">
            <UsernameInput
              verifyUsername={verifyUsername}
              onComplete={async (username) => {
                updateData({ username });
                const success = await createPlayer(username);
                if (success) {
                  nextStep();
                }
              }}
            />
          </RPGConversationLayout>
        );

      case "celebi_welcome_back":
        return (
          <RPGConversationLayout
            onAdvance={nextStep}
            text={`Alright ${data.username} from ${getCountryName(
              data.country || "",
            )}, nice to meet you. You are going to be a valuable ally in this war.`}
          />
        );

      case "celebi_gift_intro":
        return (
          <RPGConversationLayout
            onAdvance={nextStep}
            text="But before I can introduce you to the road ahead, there is one thing yet to be done. Come back here, once you have 8 allies on your team. Your first two partners will be a gift from me."
          />
        );

      case "starter_picker":
        return (
          <div className="w-full h-full flex flex-col items-center justify-center p-2 bg-pk-night-2">
            <div className="mb-8 text-center">
              <h2 className="text-xl   font-black text-pk-yellow-1 mb-2 uppercase italic">
                A Gift from Celebi
              </h2>
              <p className="text-pk-gray-3 text-xs">
                Select your affinity to reveal your first partner
              </p>
            </div>
            <StarterTypePicker onSelect={onSelectStarter} />
          </div>
        );

      case "starter_reveal":
        const currentStarter = data.starters?.[currentRevealIndex];
        const isLastStarter =
          currentRevealIndex === (data.starters?.length || 0) - 1;

        return (
          <div className="w-full h-full bg-pk-night-2">
            <StarterReveal
              key={`reveal-${currentRevealIndex}`}
              pokemonName={currentStarter?.base_pokemon_name || "Unknown"}
              activeSkills={currentStarter?.active_skills}
              onComplete={onRevealComplete}
              confirmText={isLastStarter ? "Begin Journey" : "Continue"}
            />
          </div>
        );

      case "camps_explanation":
        return (
          <div className="w-full h-full flex flex-col items-center justify-center p-8 bg-pk-night-2 text-center">
            <div className="max-w-sm flex flex-col gap-6">
              <div className="w-20 h-20 bg-pk-yellow-1/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-4xl">⚔️</span>
              </div>
              <h2 className="text-3xl font-black text-white italic uppercase tracking-tighter">
                Alpha Access
              </h2>
              <div className="flex flex-col gap-4 text-pk-gray-4 leading-relaxed">
                <p>
                  To play the game, you need to be part of a{" "}
                  <strong>Camp</strong> (Telegram Group) and have{" "}
                  <strong>Alpha Access</strong>.
                </p>
                <p>
                  You will now choose if you want to create your own camp or join
                  an existing one.
                </p>
                <p>
                  An admin will help you with the setup and granting access.
                </p>
              </div>
              <Button variant="default" onClick={nextStep} className="mt-4">
                <span className="px-12 py-2">UNDERSTOOD</span>
              </Button>
            </div>
          </div>
        );

      case "camp_picker":
        return (
          <div className="w-full h-full flex flex-col items-center justify-center p-6 bg-pk-night-2">
            <div className="flex flex-col items-center gap-6 p-8 bg-black/60 backdrop-blur-md rounded-3xl border border-white/10 max-w-md w-full">
              <h2 className="text-2xl font-black italic text-white text-center">
                CHOOSE YOUR PATH
              </h2>
              <div className="flex flex-col gap-4 w-full">
                <Button
                  variant="blue"
                  onClick={async () => {
                    updateData({ campSelectionType: "existing" });
                    await completeOnboarding();
                  }}
                >
                  JOIN EXISTING CAMP
                </Button>
                <Button
                  variant="default"
                  onClick={async () => {
                    updateData({ campSelectionType: "recommended" }); // Using recommended as "create" for now or add new type
                    await completeOnboarding();
                  }}
                >
                  CREATE GROUP FOR FRIENDS
                </Button>
              </div>
            </div>
          </div>
        );

      case "finished":
        const isCreating = data.campSelectionType === "recommended";
        const adminMessage = isCreating
          ? `Hi! I finished onboarding. I want to create a camp for my friends. My username is @${data.username}.`
          : `Hi! I finished onboarding. I want to join an existing camp. My username is @${data.username}.`;

        return (
          <div className="flex flex-col items-center gap-8 p-8 bg-black/60 backdrop-blur-md rounded-3xl border border-white/10 max-w-md w-full text-center">
            <h2 className="text-3xl font-black italic text-white">
              FINAL STEP
            </h2>
            <div className="flex flex-col gap-4">
              <p className="text-pk-gray-3">
                To activate your account and camp, send this message to the
                admin:
              </p>
              <div className="bg-black/40 p-4 rounded-xl border border-white/10 text-left select-all cursor-text">
                <p className="text-pk-blue-3 font-mono text-sm">
                  {adminMessage}
                </p>
              </div>
            </div>
            <Button
              variant="default"
              onClick={() => {
                // Open admin chat
                window.open(
                  `https://t.me/wayto66?text=${encodeURIComponent(
                    adminMessage,
                  )}`,
                  "_blank",
                );
              }}
              className="w-full"
            >
              <span className="xs:text-lg font-black tracking-wide px-8 xs:px-16 py-8">
                MESSAGE ADMIN
              </span>
            </Button>
          </div>
        );

      case "intro":
        return <IntroView nextStep={nextStep} />;

      default:
        return <div>Unknown step: {step}</div>;
    }
  };

  return (
    <main
      className="w-full h-full  bg-pk-night-2 flex items-center justify-center relative overflow-hidden"
      style={{
        height: "min(100svh, calc(480px * 18 / 9))",
        maxWidth: "480px",
      }}
    >
      <div className="relative z-10 w-full h-full flex justify-center items-center">
        {renderStep()}
      </div>
    </main>
  );
};
